import React, {useEffect, useMemo, useState} from 'react'
import styled from 'styled-components'

import {useNavigation} from 'react-navi'
import {useAppState} from '../../../state'
import {useTranslation} from 'react-i18next'

import {MainLayoutWithoutStretch, MainContent, RightSidebar} from '../../Layout/Layout'
import {InvisibleContainer, Baseline, Grid} from '../../Layout/Grid'
import {GoBackHeaderTitle, HeaderRow} from '../../Molecules/ViewComponents/ViewHeader'
import {BigLabelInput, Label, LabelInput, LabelTextArea, Select} from '../../Atoms/Forms'
import {Button, ButtonRowWrap, ButtonRowGrid} from '../../Atoms/Buttons'
import {Text} from '../../Atoms/Typography'
import SiteOrganisationList from '../../Organisms/SiteOrganisationList'
import {Helmet} from 'react-navi-helmet-async'
import {isEmpty} from 'lodash'
import {Switch} from '../../../sharedComponents/components'
import AssetUploaderSmall from '../../Molecules/UploadImage'
import {IconCloseSmall} from '../../../Assets/Icons/TinyIcons'
import {colors} from '../../../sharedComponents/colors'

interface EditChainProps {
  id?: string
}

const AROMI_PASSWORD_MASK = '******'

const FormLayout = styled.div`
  width: 100%;
  max-width: 60rem;
`

const FieldStack = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 1.5rem;
`

const BannerUploaderWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`

const BannerPreviewContainer = styled.div`
  width: fit-content;
  max-width: 100%;
  height: 140px;
  border: 1px solid ${colors.system.grey_5};
  border-radius: 8px;
  overflow: hidden;
  position: relative;

  img {
    width: auto;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`

const RemoveBannerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid ${colors.system.grey_5};
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  padding: 0;
  position: absolute;
  right: 4px;
  top: 4px;
`

const BannerPreview = ({assetId}: {assetId: string}) => {
  const {actions} = useAppState()
  const [url, setUrl] = useState<string>('')

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const data = await actions.getDownloadUrl(assetId)
        if (mounted) {
          setUrl(data?.downloadURL || '')
        }
      } catch (e) {
        if (mounted) {
          setUrl('')
        }
      }
    }
    if (assetId) {
      load()
    }
    return () => {
      mounted = false
    }
  }, [actions, assetId])

  if (!url) {
    return null
  }

  return <img src={url} alt="banner" />
}

const EditChainView = ({id}: EditChainProps) => {
  const nav = useNavigation()
  const {state, actions, effects} = useAppState()
  const {t} = useTranslation('locations')
  const chain = id ? state.chains.find(c => c.id === id) : undefined
  const isEditing = !!id
  const [error, setError] = useState('')
  const [name, setName] = useState(chain ? chain.name : '')
  const [description, setDescription] = useState(chain ? chain.description : '')
  const [wastePrice, setWastePrice] = useState<string>(
    chain?.wastePrice !== undefined && chain?.wastePrice !== null ? String(chain.wastePrice) : ''
  )
  const [aromiLink, setAromiLink] = useState(chain?.aromiLink ? chain.aromiLink : '')
  const [aromiUsername, setAromiUsername] = useState(chain?.aromiUsername ? chain.aromiUsername : '')
  const [aromiPassword, setAromiPassword] = useState(chain?.aromiPassword ? chain.aromiPassword : '')
  const [region, setRegion] = useState<string>(chain?.region || 'FI')
  const [currency, setCurrency] = useState<string>(chain?.wasteCurrency || 'EUR')
  const [completedByMandatoryForAllTasks, setCompletedByMandatoryForAllTasks] = useState<boolean>(
    chain?.completedByMandatoryForAllTasks ?? false
  )
  const [resolvedByMandatoryForAllAlarms, setResolvedByMandatoryForAllAlarms] = useState<boolean>(
    chain?.resolvedByMandatoryForAllAlarms ?? false
  )
  const [applianceWebstoreEnabled, setApplianceWebstoreEnabled] = useState<boolean>(
    chain?.applianceWebstoreEnabled ?? false
  )
  const [applianceWebstoreCustomerId, setApplianceWebstoreCustomerId] = useState<string>(
    chain?.applianceWebstoreCustomerId || ''
  )
  const [applianceWebstoreBannerAssetId, setApplianceWebstoreBannerAssetId] = useState<string>(
    chain?.applianceWebstoreBannerAssetId || ''
  )
  const [regions, setRegions] = useState<
    {id: string; code: string; name: string; currency: string; defaultWastePrice: number}[]
  >([])
  const [wastePriceByRegion, setWastePriceByRegion] = useState<Record<string, string>>({[region]: wastePrice})
  const isSuperUser = state.me?.accessRights.superuser === true
  const isChainManager = chain
    ? state.me?.user.userToSite.some(uts => uts.userRole === 'CM' && uts.chainId === chain.id)
    : false
  const canSave = !!(isSuperUser || isChainManager)

  useEffect(() => {
    const load = async () => {
      try {
        const data = await effects.adminApi.getRegions()
        setRegions(data.items)
      } catch (e) {
        // fallback: Finland, Sweden, Norway
        setRegions([
          {id: 'fi', code: 'FI', name: 'Finland', currency: 'EUR', defaultWastePrice: 3.0},
          {id: 'se', code: 'SE', name: 'Sweden', currency: 'SEK', defaultWastePrice: 65.0},
          {id: 'no', code: 'NO', name: 'Norway', currency: 'NOK', defaultWastePrice: 70.0}
        ])
      }
    }
    load()
  }, [effects.adminApi])

  useEffect(() => {
    setName(chain?.name || '')
    setDescription(chain?.description || '')
    setWastePrice(chain?.wastePrice !== undefined && chain?.wastePrice !== null ? String(chain.wastePrice) : '')
    setAromiLink(chain?.aromiLink || '')
    setAromiUsername(chain?.aromiUsername || '')
    setAromiPassword(chain?.aromiPassword || '')
    setRegion(chain?.region || 'FI')
    setCurrency(chain?.wasteCurrency || 'EUR')
    setApplianceWebstoreEnabled(chain?.applianceWebstoreEnabled ?? false)
    setApplianceWebstoreCustomerId(chain?.applianceWebstoreCustomerId || '')
    setApplianceWebstoreBannerAssetId(chain?.applianceWebstoreBannerAssetId || '')
  }, [
    chain?.id,
    chain?.name,
    chain?.description,
    chain?.wastePrice,
    chain?.aromiLink,
    chain?.aromiUsername,
    chain?.aromiPassword,
    chain?.region,
    chain?.wasteCurrency,
    chain?.applianceWebstoreEnabled,
    chain?.applianceWebstoreCustomerId,
    chain?.applianceWebstoreBannerAssetId
  ])

  useEffect(() => {
    const r = regions.find(r => r.code === region)
    if (r) {
      setCurrency(r.currency)
      const existing = wastePriceByRegion[region]
      if (existing !== undefined) {
        setWastePrice(existing)
      } else {
        setWastePrice(String(r.defaultWastePrice))
      }
    }
  }, [region, regions, wastePriceByRegion])

  const regionOptions = useMemo(() => regions.map(r => ({id: r.code, option: r.name})), [regions])

  const sanitizeCustomerId = (value: string): string => value.replace(/[^a-zA-Z0-9]/g, '')

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!canSave) {
      setError(t('locations:errors.noPermission', 'You do not have permission to save changes'))
      return
    }
    const num = validateInput(wastePrice, true)
    if (num === null) return
    if (isEditing) {
      try {
        await actions.updateChain({
          ...chain!,
          name,
          description,
          wastePrice: num,
          aromiLink,
          aromiUsername,
          aromiPassword,
          region,
          wasteCurrency: currency,
          completedByMandatoryForAllTasks,
          resolvedByMandatoryForAllAlarms,
          applianceWebstoreEnabled,
          applianceWebstoreCustomerId: applianceWebstoreEnabled ? applianceWebstoreCustomerId : null,
          applianceWebstoreBannerAssetId: applianceWebstoreEnabled ? applianceWebstoreBannerAssetId || null : null
        })
        nav.goBack()
      } catch (error) {}
    } else {
      try {
        await actions.createChain({
          chain: {
            name,
            description,
            aromiLink, aromiUsername, aromiPassword,
            completedByMandatoryForAllTasks,
            resolvedByMandatoryForAllAlarms,
            applianceWebstoreEnabled,
            applianceWebstoreCustomerId: applianceWebstoreEnabled ? applianceWebstoreCustomerId : null,
            applianceWebstoreBannerAssetId: applianceWebstoreEnabled ? applianceWebstoreBannerAssetId || null : null
          },
          site: {
            name: 'some site',
            temperatureUnit: 'C',
            weightUnit: 'G',
            locale: 'fi-FI',
            timeZone: 'Europe/Helsinki'
          }
        })
        // Skip list refresh to avoid 403; parent view can reload as needed
        nav.goBack()
      } catch (e) {}
    }
  }

  const validateInput = (value: string, enforceRequired: boolean = false): number | null => {
    if (isEmpty(value)) {
      if (enforceRequired) {
        setError(t('locations:errors.required', 'Waste price is required'))
        return null
      }
      setWastePrice('')
      setWastePriceByRegion(prev => ({...prev, [region]: ''}))
      setError('')
      return null
    }
    const num = parseFloat(value)
    if (!isNaN(num) && num > 0) {
      setWastePrice(value)
      setWastePriceByRegion(prev => ({...prev, [region]: value}))
      setError('')
      return num
    } else if (!isNaN(num) && num <= 0) {
      setError(`${t('locations:errors.notNegative', 'Value cannot be a negative number or a zero')}`)
      return null
    } else {
      setError(`${t('locations:errors.mustBeNumber', 'Invalid input, value must be a number')}`)
      return null
    }
  }

  return (
    <MainLayoutWithoutStretch>
      {isEditing ? (
        <Helmet title={t('common:routes.editChain', 'Edit chain')} />
      ) : (
        <Helmet title={t('common:routes.createChain', 'Create chain')} />
      )}
      <MainContent variant="white">
        <HeaderRow>
          <GoBackHeaderTitle
            path="/settings/locations"
            label={
              isEditing
                ? `${t('locations:labels.editChain', 'Edit chain')}`
                : `${t('locations:labels.newChain', 'New chain')}`
            }
            backLabel={t('locations:labels.locations', 'Location settings')}
          />
        </HeaderRow>
        <InvisibleContainer>
          <Baseline>
            <form onSubmit={handleSave} autoComplete="off">
              <FormLayout>
                <Grid gap="2rem">
                  <FieldStack>
                    <BigLabelInput
                      labelText={t('locations:labels.chainName', 'Chain name')}
                      placeholder={t('locations:placeholders.chainName', 'Chain name')}
                      value={name}
                      onChange={e => setName(e.target.value)}
                    />
                    <LabelTextArea
                      labelText={t('locations:labels.description', 'Description')}
                      onChange={e => setDescription(e.target.value)}
                      value={description}
                    />
                    <Select
                      returnId={true}
                      nativeProps={{
                        value: region,
                        onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
                          setRegion(e.target.value)
                        }
                      }}
                      id="region"
                      label={t('locations:labels.region', 'Region')}
                      options={regionOptions}
                    />
                    <LabelInput
                      name="wastePrice"
                      labelText={t('common:general.price', `Waste price`) + ` ${currency}/KG`}
                      value={wastePrice}
                      onChange={e => validateInput(e.target.value)}
                      errorMsg={error}
                      type="number"
                      step="0.01"
                    />
                    <LabelInput
                      name="aromiLink"
                      labelText={t('locations:labels.addAromiLink', 'Add aromi link')}
                      value={aromiLink}
                      onChange={e => setAromiLink(e.target.value)}
                    />
                    <LabelInput
                      name="aromiServiceUsername"
                      labelText={t('locations:labels.aromiUsername', 'Aromi username')}
                      value={aromiUsername}
                      autoComplete="off"
                      onChange={e => setAromiUsername(e.target.value)}
                    />
                    <LabelInput
                      name="aromiServicePassword"
                      labelText={t('locations:labels.aromiPassword', 'Aromi password')}
                      type="password"
                      value={aromiPassword}
                      autoComplete="new-password"
                      placeholder={isEditing && chain?.aromiPassword ? AROMI_PASSWORD_MASK : ''}
                      onChange={e => setAromiPassword(e.target.value)}
                    />
                  </FieldStack>
                  <div>
                    <ToggleGroup>
                      <ToggleRow>
                        <ToggleLabel>
                          {t(
                            'locations:labels.completedByMandatoryForAllTasks',
                            'Completed by field is mandatory for all tasks'
                          )}
                        </ToggleLabel>
                        <Switch
                          data-cy="chain-toggle-completed-by-mandatory"
                          name="completedByMandatoryForAllTasks"
                          checked={completedByMandatoryForAllTasks}
                          onChange={() => setCompletedByMandatoryForAllTasks(!completedByMandatoryForAllTasks)}
                        />
                      </ToggleRow>
                      <ToggleRow>
                        <ToggleLabel>
                          {t(
                            'locations:labels.resolvedByMandatoryForAllAlarms',
                            'Resolved by field is mandatory for all alarms'
                          )}
                        </ToggleLabel>
                        <Switch
                          data-cy="chain-toggle-resolved-by-mandatory"
                          name="resolvedByMandatoryForAllAlarms"
                          checked={resolvedByMandatoryForAllAlarms}
                          onChange={() => setResolvedByMandatoryForAllAlarms(!resolvedByMandatoryForAllAlarms)}
                        />
                      </ToggleRow>
                      {isSuperUser && (
                        <>
                          <ToggleRow>
                            <div>{t('locations:labels.enableApplianceWebstore', 'Enable appliance webstore')}</div>
                            <Switch
                              data-cy="chain-toggle-enable-appliance-webstore"
                              name="applianceWebstoreEnabled"
                              checked={applianceWebstoreEnabled}
                              onChange={() => setApplianceWebstoreEnabled(!applianceWebstoreEnabled)}
                            />
                          </ToggleRow>
                          {applianceWebstoreEnabled && (
                            <FieldStack style={{marginTop: '1.5rem'}}>
                              <LabelInput
                                name="applianceWebstoreCustomerId"
                                labelText={t('locations:labels.webstoreCustomerId', 'Customer ID')}
                                placeholder={t('locations:placeholders.webstoreCustomerId', 'Enter customer ID')}
                                value={applianceWebstoreCustomerId}
                                onChange={e => setApplianceWebstoreCustomerId(sanitizeCustomerId(e.target.value))}
                              />
                              <div>
                                <Label>{t('locations:labels.landingPageBannerImage', 'Landing page banner image')}</Label>
                                <BannerUploaderWrap>
                                  <AssetUploaderSmall
                                    onAssetSaved={asset => {
                                      setApplianceWebstoreBannerAssetId(asset?.id || '')
                                    }}
                                  />
                                  {!!applianceWebstoreBannerAssetId && (
                                    <BannerPreviewContainer>
                                      <BannerPreview assetId={applianceWebstoreBannerAssetId} />
                                      <RemoveBannerButton
                                        type="button"
                                        onClick={e => {
                                          e.preventDefault()
                                          setApplianceWebstoreBannerAssetId('')
                                        }}
                                        aria-label={t('common:buttons.remove', 'Remove')}
                                      >
                                        <IconCloseSmall />
                                      </RemoveBannerButton>
                                    </BannerPreviewContainer>
                                  )}
                                </BannerUploaderWrap>
                              </div>
                            </FieldStack>
                          )}
                        </>
                      )}
                    </ToggleGroup>
                  </div>
                </Grid>
              </FormLayout>
              <ButtonRowWrap>
                <div className="empty"></div>
                <ButtonRowGrid>
                  <Button variant="secondary" negative onClick={() => nav.goBack()}>
                    {t('common:buttons.cancel', 'Cancel')}
                  </Button>
                  <Button variant="secondary" type="submit" disabled={!canSave}>
                    {t('common:buttons.save', 'Save')}
                  </Button>
                </ButtonRowGrid>
              </ButtonRowWrap>
            </form>
          </Baseline>
        </InvisibleContainer>
      </MainContent>
      {!!chain && (
        <RightSidebar>
          <InvisibleContainer style={{marginTop: '4rem'}}>
            <Baseline margin="1rem">
              <Text level={3}>{t('locations:labels.organisationHierarchy', 'Organisation hierarchy')}</Text>
              <div>
                <SiteOrganisationList data={chain} />
              </div>
            </Baseline>
          </InvisibleContainer>
        </RightSidebar>
      )}
    </MainLayoutWithoutStretch>
  )
}

const ToggleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`

const ToggleLabel = styled.div`
  max-width: 75%;
`

export default EditChainView
